import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { useEffect, useState, Suspense } from 'react';
import ProtectedAdminRoute from './ProtectedAdminRoute';
import LanguageWrapper from './LanguageWrapper';
import { ANIMATION } from '@/config/constants';
import {
  coreRoutes,
  serviceRoutes,
  locationRoutes,
  blogRoutes,
  discountRoutes,
  referralRoutes,
  programRoutes,
  partnerPortalRoutes,
  adminRoutes,
  redirectRoutes,
  notFoundRoute,
  toSpanishPath,
  type RouteConfig,
} from '@/config/routes';

// Page transition variants
const pageVariants: Variants = {
  initial: { opacity: 0 },
  enter: {
    opacity: 1,
    transition: {
      duration: ANIMATION.PAGE_TRANSITION_MS / 1000,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: (ANIMATION.PAGE_TRANSITION_MS * 0.67) / 1000,
      ease: 'easeIn',
    },
  },
};

const reducedMotionVariants: Variants = {
  initial: { opacity: 0 },
  enter: {
    opacity: 1,
    transition: { duration: ANIMATION.REDUCED_MOTION_TRANSITION_MS / 1000 },
  },
  exit: {
    opacity: 0,
    transition: { duration: (ANIMATION.REDUCED_MOTION_TRANSITION_MS * 0.67) / 1000 },
  },
};

interface PageWrapperProps {
  children: React.ReactNode;
}

const PageWrapper = ({ children }: PageWrapperProps) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const variants = prefersReducedMotion ? reducedMotionVariants : pageVariants;

  return (
    <motion.div
      initial="initial"
      animate="enter"
      exit="exit"
      variants={variants}
      style={{ willChange: 'opacity' }}
    >
      {children}
    </motion.div>
  );
};

const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * Renders a route with PageWrapper
 */
const renderRoute = (route: RouteConfig) => {
  const element = <PageWrapper>{route.element}</PageWrapper>;

  if (route.protected) {
    return (
      <Route
        key={route.path}
        path={route.path}
        element={<ProtectedAdminRoute>{element}</ProtectedAdminRoute>}
      />
    );
  }

  return <Route key={route.path} path={route.path} element={element} />;
};

/**
 * Renders Spanish version of a route
 */
const renderSpanishRoute = (route: RouteConfig) => {
  const spanishPath = toSpanishPath(route.path);
  return (
    <Route
      key={spanishPath}
      path={spanishPath}
      element={<PageWrapper>{route.element}</PageWrapper>}
    />
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();

  // Combine all public routes (excluding dynamic routes for Spanish)
  const publicRoutesForSpanish = [
    ...coreRoutes,
    ...serviceRoutes,
    ...locationRoutes,
    ...discountRoutes,
    ...referralRoutes,
    ...programRoutes,
  ].filter((route) => !route.path.includes(':'));

  return (
    <LanguageWrapper>
      <AnimatePresence mode="wait">
        <Suspense fallback={<PageLoading />}>
          <Routes location={location} key={location.pathname}>
            {/* English public routes */}
            {coreRoutes.map(renderRoute)}
            {serviceRoutes.map(renderRoute)}
            {locationRoutes.map(renderRoute)}
            {blogRoutes.map(renderRoute)}
            {discountRoutes.map(renderRoute)}
            {referralRoutes.map(renderRoute)}
            {programRoutes.map(renderRoute)}
            {partnerPortalRoutes.map(renderRoute)}

            {/* Spanish routes */}
            {publicRoutesForSpanish.map(renderSpanishRoute)}

            {/* Admin routes */}
            {adminRoutes.map(renderRoute)}

            {/* Redirects */}
            {redirectRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}

            {/* 404 */}
            {renderRoute(notFoundRoute)}
          </Routes>
        </Suspense>
      </AnimatePresence>
    </LanguageWrapper>
  );
};

export default AnimatedRoutes;
